/**
 * Hollow-effect detection contracts for the Legendary Arena game engine
 * (WP-257 / D-24033 + D-24034).
 *
 * A *hollow* effect is a declared card ability whose executable handler is
 * absent or unreachable at runtime. The detector classifies on handler
 * REACHABILITY — never by diffing pre/post `G`. An empty-bystander-supply
 * rescue, a failed `[hc:]`/`[team:]` condition, an empty-deck reveal, and an
 * explicitly-deferred mechanic are all reachable handlers that legitimately
 * no-op; they are NOT hollow.
 *
 * This module is the contract surface only: the reason taxonomy + its
 * drift-protected canonical array, the hollow-reason predicate, the internal
 * per-effect outcome shape both executors build, the serializable runtime
 * record, the `G.diagnostics` channel shape, the bounded-channel cap, and the
 * explicit deferred allowlist. The writer lives in `hollowEffect.record.ts`.
 *
 * No boardgame.io imports. No registry imports. No I/O. Contracts only.
 */
// why: canonical drift array (D-24033) — adding a reason requires updating THIS
// array, the EffectExecutionReason union, AND a DECISIONS.md entry together
// (code-style §Drift Detection), mirroring REVEAL_ACTION_KINDS. The drift test
// in hollowEffect.test.ts pins bidirectional parity. The seven-member order is
// locked: the four reachable reasons first, then the three hollow reasons.
/** All effect-execution reasons in canonical order. Single source of truth. */
export const EFFECT_EXECUTION_REASONS = [
    'applied',
    'handler-noop',
    'condition-failed',
    'deferred',
    'no-handler',
    'unsupported-keyword',
    'parse-unrecognized',
];
/**
 * Returns whether a reason flags the effect as hollow.
 *
 * Exactly three reasons are hollow — `parse-unrecognized`, `no-handler`, and
 * `unsupported-keyword` — all of which mean dispatch could not reach an
 * executable handler. The four reachable reasons (`applied`, `handler-noop`,
 * `condition-failed`, `deferred`) are legitimate implemented outcomes and never
 * flag.
 *
 * @param reason - The per-effect execution reason.
 * @returns Whether the reason indicates a hollow (unreachable-handler) outcome.
 */
export function isHollowReason(reason) {
    return (reason === 'parse-unrecognized' ||
        reason === 'no-handler' ||
        reason === 'unsupported-keyword');
}
// why: the bound mirrors the arena-client diagnostics ring buffer — large enough
// that a normal match never drops a real hollow event, small enough that a
// pathological match cannot grow G without limit. The exact value is not
// load-bearing for gameplay (the channel is never gameplay input); 256 leaves
// generous headroom over the few-dozen distinct hollow mechanics a match could
// realistically encounter.
/** Maximum number of HollowEffectRecord entries retained in G.diagnostics. */
export const HOLLOW_EFFECTS_CAP = 256;
// why: D-24033 — "deferred = not hollow" holds ONLY for mechanics on this
// explicit allowlist. `wound` and `conditional` have NO handler today (absent
// from HANDLED_KEYWORDS); without this allowlist they would classify as
// `no-handler` → hollow even though they are implemented-as-deferred by design
// (they need targeting UI / game systems not yet built). Listing them here
// classifies them `deferred` (reachable, not hollow). A future WP that ships a
// real handler removes the mechanic from this set.
/** Mechanics that are deferred-by-design — classified `deferred`, never hollow. */
export const DEFERRED_BY_DESIGN_MECHANICS = new Set([
    'wound',
    'conditional',
]);
// why: canonical drift array (WP-488 / D-24294) — adding a status requires updating
// THIS array, the EffectTraceStatus union, AND a DECISIONS.md entry together
// (code-style §Drift Detection). The drift test in effectTrace.record.test.ts pins
// bidirectional parity, mirroring EFFECT_EXECUTION_REASONS above.
/** All effect-trace statuses in canonical order. Single source of truth. */
export const EFFECT_TRACE_STATUSES = [
    'fired',
    'no-op',
    'no-handler',
    'secondary-site',
];
// why: canonical drift array (WP-488 / D-24294) — same bidirectional-parity contract
// as EFFECT_TRACE_STATUSES; a fire site added to the union but not the array (or vice
// versa) fails the drift test.
/** All effect-trace fire sites in canonical order. Single source of truth. */
export const EFFECT_TRACE_FIRE_SITES = [
    'villain-executor',
    'hero-executor',
    'hero-primitive',
    'escape-scheme-twist',
];
// why: WP-488 / D-24294 — a trace fires on EVERY effect dispatch (unlike the rare
// hollow record), so a long match is high-volume; the cap bounds `G.diagnostics.traces`
// so a pathological match cannot grow G without limit (mirrors HOLLOW_EFFECTS_CAP + the
// arena-client diagnostics ring buffer). The value is not gameplay-load-bearing (the
// channel is never gameplay input); 512 leaves generous headroom for a long match's
// dispatch volume while staying bounded.
/** Maximum number of EffectTrace entries retained in G.diagnostics. */
export const EFFECT_TRACES_CAP = 512;
//# sourceMappingURL=hollowEffect.types.js.map